import { useEffect, useRef, useState } from "react";
import { normalizeCanvasSizePreset, resolveCanvasRect } from "@refinement-canvas/domain";
import { useCanvasDocStore } from "./store/canvas-doc-store";
import { useCanvasApi } from "../../lib/canvas-api";
import { useOgImageResolver } from "../../lib/og-image-source";
import { saveCanvasThumbnail } from "../../lib/canvas-thumbnail-cache";
import { dataUrlToPngBlob, renderCanvasThumbnailDataUrl } from "../../lib/canvas-thumbnail";
import { getWebConfig } from "../../env";

const UPLOAD_DEBOUNCE_MS = 2_500;

// 図形の連続変更（ドラッグ等）で非同期描画が並走しないよう、描画開始を遅延させる。
// アップロードのデバウンス（2,500ms）より短く、体感の遅れが出ない値にする。
const RENDER_DEBOUNCE_MS = 1_200;

export function useCanvasThumbnail(canvasId: string): string | undefined {
  const activeCanvasId = useCanvasDocStore((s) => s.canvasId);
  const objects = useCanvasDocStore((s) => s.objects);
  const sizePreset = normalizeCanvasSizePreset(useCanvasDocStore((s) => s.meta.sizePreset));
  const resyncPending = useCanvasDocStore((s) => s.resyncPending);
  const isTrashed = useCanvasDocStore((s) => s.meta.isTrashed ?? false);
  const thumbnailUrl = useCanvasDocStore((s) => s.meta.thumbnailUrl);
  const updateLocalMeta = useCanvasDocStore((s) => s.updateLocalMeta);
  const canvasApi = useCanvasApi();
  // リンクカードの画像は同一オリジンの中継経由でしか読めないため、解決関数を描画へ渡す。
  const resolveLinkCardImageUrl = useOgImageResolver();
  const [thumbnail, setThumbnail] = useState<string | undefined>();
  const latestDataUrlRef = useRef<string | undefined>(undefined);
  const uploadTimerRef = useRef<number | undefined>(undefined);
  const uploadGenerationRef = useRef(0);
  const renderTimerRef = useRef<number | undefined>(undefined);
  const renderGenerationRef = useRef(0);
  // 進行中の描画を中断するためのコントローラ。世代ガードは「完了した結果を捨てる」だけで、
  // 開始済みの画像読み込み・描画・PNG エンコードを止めないため、別に持つ。
  const renderAbortRef = useRef<AbortController | null>(null);
  // localStorage への保存可否の判定にだけ使うため、effect の依存には入れない
  // （依存へ入れると、アップロード成功による thumbnailUrl の更新で描画がもう 1 周する）。
  const thumbnailUrlRef = useRef(thumbnailUrl);
  useEffect(() => {
    thumbnailUrlRef.current = thumbnailUrl;
  }, [thumbnailUrl]);

  useEffect(() => {
    if (activeCanvasId !== canvasId) return;
    // 初回 stale bootstrap 無視後の resync 待ち中は、objects が権威スナップショット未取り込みの
    // 乖離状態（ほぼ空など）であり得るため、サムネイルの生成・アップロードを起動しない
    // （resync 完了の loadSnapshot 全適用でフラグが下り、権威状態から再開する）。
    if (resyncPending) return;
    const canvasRect = resolveCanvasRect(sizePreset);
    // 遅延中に store が変わっても「この時点の内容」を描く（次の変更は次の effect が描き直す）。
    const snapshot = Object.values(objects);
    if (renderTimerRef.current !== undefined) {
      window.clearTimeout(renderTimerRef.current);
    }
    // 新しい描画を始める前に、進行中の描画を中断する。
    renderAbortRef.current?.abort();
    const generation = (renderGenerationRef.current += 1);
    renderTimerRef.current = window.setTimeout(() => {
      renderTimerRef.current = undefined;
      const controller = new AbortController();
      renderAbortRef.current = controller;
      // 未処理 rejection でサムネイル更新が止まらないよう、必ず catch する。
      void (async () => {
        const dataUrl = await renderCanvasThumbnailDataUrl(snapshot, canvasRect, {
          signal: controller.signal,
          resolveLinkCardImageUrl,
        });
        // 中断された場合は null が返る。世代ガードより先に判定して早期に抜ける。
        if (dataUrl === null) return;
        // 遅い描画の完了が新しい描画の結果を上書きしないよう、最新世代だけが反映する。
        if (generation !== renderGenerationRef.current) return;
        if (!dataUrl) return;
        latestDataUrlRef.current = dataUrl;
        setThumbnail(dataUrl);
        // S3 の代替（thumbnailUrl）が無いなら、保存に失敗しても既存キャッシュを消さない。
        // mock 認証だけでなく、初回アップロード前とアップロード失敗中も代替が無い。
        saveCanvasThumbnail(canvasId, dataUrl, { keepOnFailure: !thumbnailUrlRef.current });

        // ゴミ箱では表示用生成とローカルキャッシュだけを続け、サーバ保存は起動しない。
        if (isTrashed) return;

        if (uploadTimerRef.current !== undefined) {
          window.clearTimeout(uploadTimerRef.current);
        }
        // 並行アップロードの古い完了通知が最新サムネイルのメタ情報を戻さないよう、
        // タイマーを予約した時点の世代だけがメタ更新できるようにする。
        const uploadGeneration = (uploadGenerationRef.current += 1);
        uploadTimerRef.current = window.setTimeout(() => {
          uploadTimerRef.current = undefined;
          void uploadThumbnail(
            canvasApi,
            canvasId,
            latestDataUrlRef.current,
            updateLocalMeta,
            () => uploadGeneration === uploadGenerationRef.current
          );
        }, UPLOAD_DEBOUNCE_MS);
      })().catch((error: unknown) => {
        // 描画の失敗はサムネイルが更新されないだけで編集は続けられる。次の変更で再試行される。
        console.warn("[thumbnail] サムネイルの描画に失敗しました", error);
      });
    }, RENDER_DEBOUNCE_MS);

    return () => {
      // 世代を進めて、進行中の描画の完了を反映させない。
      renderGenerationRef.current += 1;
      // 併せて中断し、開始済みの読み込み・描画・エンコードを止める。
      renderAbortRef.current?.abort();
      renderAbortRef.current = null;
      if (renderTimerRef.current !== undefined) {
        window.clearTimeout(renderTimerRef.current);
        renderTimerRef.current = undefined;
      }
      uploadGenerationRef.current += 1;
      if (uploadTimerRef.current !== undefined) {
        window.clearTimeout(uploadTimerRef.current);
        uploadTimerRef.current = undefined;
      }
    };
  }, [
    activeCanvasId,
    resyncPending,
    isTrashed,
    canvasApi,
    canvasId,
    objects,
    resolveLinkCardImageUrl,
    updateLocalMeta,
    sizePreset,
  ]);

  return thumbnail;
}

async function uploadThumbnail(
  canvasApi: ReturnType<typeof useCanvasApi>,
  canvasId: string,
  dataUrl: string | undefined,
  updateLocalMeta: (meta: { thumbnailKey?: string | undefined; thumbnailUrl?: string | undefined }) => void,
  isLatestUpload: () => boolean
): Promise<void> {
  if (!dataUrl) return;
  // mock ローカルでは S3 が無いことがあるため、TOP 表示用の localStorage キャッシュだけ更新する。
  if (getWebConfig().authMode === "mock") return;
  const blob = dataUrlToPngBlob(dataUrl);
  if (!blob) return;
  try {
    const presigned = await canvasApi.presignThumbnailUpload({
      canvasId,
      contentLength: blob.size,
    });
    const uploadResponse = await fetch(presigned.uploadUrl, {
      method: "PUT",
      body: blob,
      headers: { "content-type": "image/png" },
    });
    if (!uploadResponse.ok) {
      throw new Error(`サムネイルアップロードに失敗しました: HTTP ${uploadResponse.status}`);
    }
    if (!isLatestUpload()) return;
    const result = await canvasApi.updateCanvasMeta(canvasId, { thumbnailKey: presigned.key });
    if (!isLatestUpload()) return;
    updateLocalMeta({
      thumbnailKey: result.meta.thumbnailKey,
      thumbnailUrl: result.meta.thumbnailUrl,
    });
  } catch (error) {
    console.warn("[thumbnail] サムネイルのアップロードに失敗しました", error);
  }
}
